import { createSlice } from '@reduxjs/toolkit';
import { getProblemDetail, getProblemStatus } from 'api/v1Helpers';
import { notify } from 'components/Common/Toast';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import * as qrApi from 'services/qrApi';
import { isQrCodeLive, type QrCode } from 'types/qr';

/**
 * QR state is deliberately single-slot: one basket's ACTIVE code, for the basket-details
 * modal. There is no QR list screen. Parish-wide list/create/update thunks used to live here
 * and were removed once every code became basket-targeted — a parish-scoped lookup is the
 * defect this feature exists to prevent, so it should not sit here waiting to be re-wired.
 */
interface QRCodeState {
    /** ACTIVE code for the basket named by `inflightBasketId`. */
    activeQr: QrCode | null;
    loading: boolean;
    error: boolean;
    inflightParishId: string | null;
    /**
     * Basket the in-flight ensure belongs to. Parish alone is not an identity: one parish
     * has many baskets, and matching on parish only lets basket A's code paint on basket B.
     */
    inflightBasketId: string | null;
}

const initialState: QRCodeState = {
    activeQr: null,
    loading: false,
    error: false,
    inflightParishId: null,
    inflightBasketId: null,
};

type EnsureBasketQrResult = { qr: QrCode; parishId: string; basketId: string } | null;

/**
 * In-flight ensure per parish+basket — concurrent callers (Strict Mode, two basket modals)
 * reuse one list/create chain so we never double-POST ACTIVE codes (H1). Keyed by basket as
 * well as parish: a parish-only key hands basket B the promise resolving basket A's code.
 *
 * Per-JS-context only, and no longer the last line of defence: the backend now honours
 * `Idempotency-Key` on QR create and enforces one live code per basket with a partial unique
 * index, so a cross-tab race is a 409 that the create path adopts rather than a duplicate code.
 */
const ensureInFlightByBasket = new Map<string, Promise<EnsureBasketQrResult>>();

/** Backend max page size — widens the window in which absence can be proven (see below). */
const QR_LIST_PAGE_SIZE = 100;

/**
 * Re-list this basket's ACTIVE codes and return the one that beat us to the create.
 *
 * Scoped to the basket, so the page holds only its codes — but still matched on `basketId`
 * client-side, for the same reason the main lookup does: a dropped filter would return the whole
 * parish and `[0]` would be another basket's code.
 */
async function adoptExistingBasketQr(parishId: string, basketId: string): Promise<QrCode | null> {
    const relisted = await qrApi.listQrCodes(parishId, {
        page: 1,
        pageSize: QR_LIST_PAGE_SIZE,
        status: 'ACTIVE',
        basketId,
    });
    // Live, not merely ACTIVE: one of the cases that 409s is a code that is ACTIVE but past its
    // validUntil, and adopting that would paint a payloadUrl resolve() 404s.
    return (relisted?.items ?? []).filter((qr) => qr.basketId === basketId).find(isQrCodeLive) ?? null;
}

/**
 * List a basket's ACTIVE QR codes; create one if the basket provably has none.
 * Returns a code that will actually resolve when scanned — ACTIVE *and* unexpired.
 */
export const ensureBasketQrCode = createAsyncThunkWrapper<
    EnsureBasketQrResult,
    { parishId: string; basketId: string; label?: string; description?: string }
>(
    'qrCode/ensureBasketQrCode',
    async ({ parishId, basketId, label, description }, { getState }) => {
        if (!parishId || !basketId) return null;

        const inFlightKey = `${parishId}:${basketId}`;
        const existing = ensureInFlightByBasket.get(inFlightKey);
        if (existing) {
            return existing;
        }

        const run = (async (): Promise<EnsureBasketQrResult> => {
            try {
                // Calls the API directly rather than dispatching separate wrapped thunks —
                // those would raise their own rejected actions (and toasts) on top of this
                // thunk's own, which is how one failure used to produce three toasts.
                const listed = await qrApi.listQrCodes(parishId, {
                    page: 1,
                    pageSize: QR_LIST_PAGE_SIZE,
                    status: 'ACTIVE',
                    basketId,
                });
                if (parishSelectionMoved(getState, parishId)) return null;

                // Never trust the page to be scoped — match locally, or a basket page prints
                // another basket's code and donations go to the wrong fund.
                const forThisBasket = (listed?.items ?? []).filter((qr) => qr.basketId === basketId);
                const match = forThisBasket.find(isQrCodeLive);
                if (match) {
                    return { qr: match, parishId, basketId };
                }

                // ACTIVE but past validUntil. Expiry is enforced read-time and never flips the
                // status, so these still come back from ?status=ACTIVE — but resolve() 404s them.
                // Painting one gives a QR that prints fine and scans to nothing.
                //
                // Creating a replacement is not an option either: the code is still ACTIVE, so
                // the one-live-code-per-basket index 409s, and adopting the winner would hand
                // back the same dead row. The remedy is retire-then-reprint, which this console
                // has no affordance for — docs/tickets/open/2026-08-13-WEB-qr-retire-reprint.md.
                // Until it does, an empty state is the honest answer: there is no usable code.
                if (forThisBasket.length) return null;

                // No match is only *absence* when the page was complete. On a truncated
                // parish-wide page this basket's code may simply be off-page, and creating
                // here would mint a second printable ACTIVE code for it. Prefer an honest
                // empty state.
                const pageIsComplete = (listed?.total ?? 0) <= (listed?.items?.length ?? 0);
                if (!pageIsComplete) return null;

                // Stable key for ensure-create (retries / multi-tab share the same intent).
                const idempotencyKey = qrApi.ensureBasketQrIdempotencyKey(parishId, basketId);
                let created: QrCode;
                try {
                    created = await qrApi.createQrCode(
                        parishId,
                        {
                            label: label || 'Parish QR',
                            basketId,
                            ...(description ? { description } : { description: 'default' }),
                        },
                        idempotencyKey
                    );
                } catch (err) {
                    // A 409 means the backend's one-ACTIVE-code-per-basket index rejected this
                    // create because another writer (another tab, another device) got there
                    // first. In a list-then-create-if-absent flow that is success, not failure:
                    // the code we wanted now exists. Adopt it rather than showing an error.
                    //
                    // Branch on the status, not on `detail` — the 409 body carries prose and
                    // nothing structured, and prose gets reworded.
                    if (getProblemStatus(err) !== 409) throw err;
                    const winner = await adoptExistingBasketQr(parishId, basketId);
                    if (parishSelectionMoved(getState, parishId)) return null;
                    // Re-list found nothing: the 409 was not the race we assumed, so surface the
                    // original error rather than swallowing it into a silent empty state.
                    if (!winner) throw err;
                    return { qr: winner, parishId, basketId };
                }
                if (parishSelectionMoved(getState, parishId)) return null;
                notify('QR code created successfully', 'success');
                return { qr: created, parishId, basketId };
            } catch (err) {
                if (parishSelectionMoved(getState, parishId)) return null;
                throw err;
            } finally {
                ensureInFlightByBasket.delete(inFlightKey);
            }
        })();

        ensureInFlightByBasket.set(inFlightKey, run);
        return run;
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load QR code' }
);

/** Download the rendered QR canvas as PNG. */
export const downloadQR = (canvasElementId: string, filename: string) => {
    const canvas = document.getElementById(canvasElementId) as HTMLCanvasElement | null;
    if (!canvas) return;
    const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `${filename}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
};

const qrCodeSlice = createSlice({
    name: 'qrCode',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(ensureBasketQrCode.pending, (state, action) => {
            state.loading = true;
            state.error = false;
            // Drop the prior QR immediately so Generate cannot paint a stale payloadUrl (H2).
            state.activeQr = null;
            state.inflightParishId = action.meta.arg.parishId;
            state.inflightBasketId = action.meta.arg.basketId;
        });
        builder.addCase(ensureBasketQrCode.fulfilled, (state, action) => {
            const isInflight = state.inflightParishId === action.meta.arg.parishId && state.inflightBasketId === action.meta.arg.basketId;
            if (!action.payload) {
                if (isInflight) {
                    state.loading = false;
                }
                return;
            }
            // Both keys must match: a late response for another basket in the same parish
            // is exactly the misprint this feature exists to prevent.
            if (!isInflight || action.payload.basketId !== state.inflightBasketId) return;
            state.loading = false;
            state.activeQr = action.payload.qr;
        });
        builder.addCase(ensureBasketQrCode.rejected, (state, action) => {
            if (state.inflightParishId === action.meta.arg.parishId && state.inflightBasketId === action.meta.arg.basketId) {
                state.loading = false;
                state.error = true;
            }
        });
    },
});

export default qrCodeSlice.reducer;
